import React from "react";

import { strings } from "@perceptron/constants";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";

import { ADD_BUTTON, SET_GAP, TAB_SIZE } from "@demo/constants/media-set-layout";
import { useTapHandlers } from "@demo/hooks/useTapHandlers";

const SURFACE =
  "cursor-pointer bg-background text-foreground-secondary hover:bg-background-hover hover:text-foreground";

const GLYPH =
  "opacity-0 transition-opacity duration-200 group-hover/set:opacity-100 group-focus-within/set:opacity-100 touch:opacity-100";

export const MediaSetControls: React.FC<{
  /** True when the last row has a cell the add square can tuck into. */
  hasFreeCell: boolean;
  /** Rows occupied, counting the "+N", so the tab can centre on the last one. */
  rows: number;
  /** A phone has no hover, so the tab would stand open across a thumbnail. */
  isMobile: boolean;
  onAdd: (event: React.MouseEvent | React.TouchEvent) => void;
}> = ({ hasFreeCell, rows, isMobile, onAdd }) => {
  const tap = useTapHandlers(onAdd);

  if (isMobile) return null;

  if (hasFreeCell) {
    return (
      <div className="relative z-0 flex aspect-square w-full items-center">
        <div
          className={cn(
            // Fade as it tucks under the thumbnail; transform alone left a painted sliver in browser captures.
            "relative translate-x-0 opacity-0 ease-out [transition-duration:240ms]",
            "transition-[transform,opacity] will-change-[transform,opacity]",
            "group-hover/set:translate-x-[var(--tuck)] group-hover/set:opacity-100",
            "has-[:focus-visible]:translate-x-[var(--tuck)] has-[:focus-visible]:opacity-100",
            "touch:translate-x-[var(--tuck)] touch:opacity-100",
          )}
          style={
            {
              width: ADD_BUTTON,
              height: ADD_BUTTON,
              // Tucked behind the thumbnail to its left, and the same
              // distance back out on hover.
              marginLeft: -(ADD_BUTTON + SET_GAP),
              "--tuck": `${ADD_BUTTON + SET_GAP}px`,
            } as React.CSSProperties
          }
        >
          <button
            aria-label={strings.mediaSetCollection.addMedia}
            className={cn(
              "flex size-full items-center justify-center rounded-[10px] transition-colors duration-150",
              "border border-background-tint",
              SURFACE,
            )}
            data-testid="add-media-button"
            type="button"
            {...tap}
          >
            <Icon className="size-4" name="plus" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      aria-label={strings.mediaSetCollection.addMedia}
      className={cn(
        "absolute right-0 z-20 flex h-[44px] w-[8px] items-center justify-center overflow-hidden",
        "rounded-l-[10px] opacity-70",
        "cursor-pointer border border-r-0 border-background-tint bg-background-alt-1 hover:bg-background-hover",
        "dark:bg-popover dark:hover:bg-accent",
        "transition-[width,opacity,background-color] ease-out [transition-duration:240ms]",
        "group-hover/set:w-[44px] group-hover/set:opacity-100",
        "focus-visible:w-[44px] focus-visible:opacity-100",
        "touch:w-[44px] touch:opacity-100",
      )}
      data-testid="add-media-button"
      style={{ top: `calc(${rows - 1} * (var(--row) + ${SET_GAP}px) + var(--row) / 2 - ${TAB_SIZE / 2}px)` }}
      type="button"
      {...tap}
    >
      <span className={cn(GLYPH, "text-foreground-secondary")}>
        <Icon className="size-5" name="plus" />
      </span>
    </button>
  );
};
